import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Modal,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { toast } from "react-toastify";
import PersonIcon from "@mui/icons-material/Person";
import phone_email from "../../assets/images/landingpage/icons/phoneemail.png";
import { useTranslation } from "react-i18next";
import axios from "axios";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface ForgotPasswordModalProps {
  open: boolean;
  handleClose: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordModalProps> = ({
  open,
  handleClose,
}) => {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [userId, setUserID] = useState("");
  const [userIdError, setUserIdError] = useState("");
  const [emailOrPhoneError, setEmailOrPhoneError] = useState("");

  const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s/0-9]*$/;
  const userIdRegex = /^[a-zA-Z0-9]+$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const { t } = useTranslation();

  const handleSubmit = async () => {
    // Validate User ID
    if (userId.trim() === "") {
      setUserIdError("User ID cannot be empty.");
      return;
    }
    if (!userIdRegex.test(userId)) {
      setUserIdError("User ID can only contain letters and numbers.");
      return;
    }
    setUserIdError("");

    // Validate Email or Phone
    if (emailOrPhone.trim() === "") {
      setEmailOrPhoneError("Email / Mobile Number cannot be empty.");
      return;
    }
    if (!phoneRegex.test(emailOrPhone) && !emailRegex.test(emailOrPhone)) {
      setEmailOrPhoneError(
        "Please enter a valid email address or phone number."
      );
      return;
    }
    setEmailOrPhoneError("");

    const payload = {
      username: userId,
      emailOrPhone: emailOrPhone,
    };
    // Make API request
    try {
      const response = await axios.post(
        `${API_BASE_URL}auth/forgot-password`,
        payload
      );
      setEmailOrPhone("");
      setUserID("");
      toast.success(response.data.message);

      handleClose();
    } catch (error) {
      console.error("API Error:", error);
      // Type-safe error handling
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message;
        toast.error(errorMessage);
      } else if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        backdropFilter: "blur(3px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: {
            xs: "80%",
            sm: "70%",
            md: "450px",
            lg: "500px",
            xl: "600px",
          },
          maxWidth: "600px",
          bgcolor: "background.paper",
          borderRadius: "25px",
          boxShadow: 24,
          p: { xs: 2, sm: 3, md: 4 },
          textAlign: "center",
          height: "auto",
          position: "relative",
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          paddingBottom={2}
        >
          {/* <LockResetIcon sx={{ color: "#0d6bca", }} /> */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              color: "#0d6bca",
            }}
          >
            {t("forgot_password")}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder={t("username")}
              variant="outlined"
              value={userId}
              onChange={(e) => setUserID(e.target.value)}
              error={!!userIdError}
              helperText={userIdError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder={t("email_mobile_number")}
              variant="outlined"
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              error={!!emailOrPhoneError}
              helperText={emailOrPhoneError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={phone_email} alt="phoneemail" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "right",
            mt: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              color: "#ffffff",
              background: "#be1e2d",
              borderColor: "#be1e2d", // Make the border match the text color
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
          >
            {t("send")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ForgotPassword;
